import React from 'react';
import Button, {
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit/dist/components/button';
import LoadingSpinner from 'components/LoadingSpinner';
import './index.css';

type Variant = 'primary' | 'outline' | 'text' | 'default';

const KIT_TYPE: Record<Variant, ButtonType> = {
  primary: ButtonType.primary,
  outline: ButtonType.outline,
  // Restyled below: the kit's link buttons are green, grow-ui's text buttons
  // were neutral.
  text: ButtonType.link,
  default: ButtonType.default
};

type UiKitButtonProps = {
  variant?: Variant;
  size?: 'md' | 'lg';
  children?: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  danger?: boolean;
  ghost?: boolean;
  style?: React.CSSProperties;
  id?: string;
  'aria-label'?: string;
  'data-testid'?: string;
};

/**
 * A UI Kit button sized and coloured like the grow-ui Button it replaces:
 * 2rem (or 2.25rem large) tall with grow-ui's padding, square
 * icon-only buttons with the icon centred, and neutral text buttons.
 */
const UiKitButton = ({
  variant = 'default',
  size = 'md',
  children,
  icon,
  loading = false,
  // grow-ui's ghost is a white button with a coloured border, not antd's
  // transparent ghost (white text, for dark backgrounds), so it is styled
  // here instead of being passed to the kit.
  ghost = false,
  ...rest
}: UiKitButtonProps) => {
  const iconOnly = Boolean(icon) && React.Children.count(children) === 0;
  const className = [
    'cwf-kit-button',
    `cwf-kit-button--${size}`,
    `cwf-kit-button--${variant}`,
    iconOnly ? 'cwf-kit-button--icon-only' : '',
    ghost ? 'cwf-kit-button--ghost' : ''
  ].join(' ');

  return (
    <Button
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      type={KIT_TYPE[variant]}
      // While loading, grow-ui showed a small spinner in place of the label
      // (and icon); the kit would add its own spinner beside the label.
      icon={loading ? undefined : icon}
      className={className}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <LoadingSpinner message="" size={14} inline label="Loading" />
      ) : (
        children
      )}
    </Button>
  );
};

export default UiKitButton;
